<template>
  <div class="modal is-active">
    <div class="modal-background"></div>
    <div class="modal-card">
      <header class="modal-card-head">
        <p v-if="invitePlatformAdmins" class="modal-card-title">Add users as platform admins</p>
        <p v-else class="modal-card-title">Add {{ userTerm }}s to {{ workspace.name }}</p>
        <button v-on:click="$emit('close')" class="delete" aria-label="close"></button>
      </header>
      <section class="modal-card-body">
        <div class="enter-emails">
          <TagsInput
            element-id="whitelisted-domains"
            placeholder="Enter a list of emails..."
            :add-tags-on-space="true"
            :validation="isEmailValid"
            validation-error-message="This is not a valid email"
            v-model="emails"
          >
          </TagsInput>
        </div>
      </section>
      <footer class="modal-card-foot">
        <n-button-group>
          <n-button variant="primary" :disabled="!hasEmailAddresses()" :loading="updateInProgress" @click="update">
            Send Invite
          </n-button>
          <n-button @click="$emit('close')">Cancel</n-button>
        </n-button-group>
      </footer>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { workspacesApi } from '@/workspaces/workspaces';
import TagsInput from '@/shared/components/TagsInput.vue';
import Toast from '@/shared/services/toast';
import { PropType, ref } from 'vue';
import WorkspaceViewModel from '@/workspaces/models/workspaceViewModel';

const emailRegex: RegExp =
  /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,24}))$/;

const props = defineProps({
  workspace: {
    type: Object as PropType<WorkspaceViewModel>,
    required: false,
  },
  userTerm: {
    type: String,
    required: true,
  },
  invitePlatformAdmins: {
    type: Boolean,
    default: false,
  },
});

if (!props.invitePlatformAdmins && !props.workspace) {
  throw new Error('workspace is required when invitePlatformAdmins is false');
}

const updateInProgress = ref(false);
const emails = ref<string[]>([]);

const emit = defineEmits<{
  (e: 'close', invited?: boolean): void;
}>();

function isEmailValid(input: string) {
  return !input || emailRegex.test(input);
}

async function update(): void {
  if (updateInProgress.value) {
    return;
  }

  if (!hasEmailAddresses()) {
    Toast.showError('No valid email addresses added');
    return;
  }

  updateInProgress.value = true;
  try {
    await callApi();
    emit('close', true);
  } catch (err: any) {
    updateInProgress.value = false;
    Toast.showError(err.message);
  }
}

async function callApi() {
  if (props.invitePlatformAdmins) {
    await workspacesApi.invitePlatformAdmins(emails.value);
    Toast.showSuccess(`Successfully invited to be platform admins`);
  } else {
    await workspacesApi.inviteUsers(emails.value, props.workspace.id);
    Toast.showSuccess(`Successfully invited to ${props.workspace.name}`);
  }
}

function hasEmailAddresses() {
  return emails.value.length > 0;
}
</script>
